// Typen voor de Sprekersmodule (Fase 1). Mirror van de Pydantic-schemas in
// backend/sprekers/schemas.py -- alleen velden die de UI daadwerkelijk
// gebruikt. Zie docs/sprekers-Module.md.

export type SpeakerType = 'intern' | 'extern';
export type AvailabilityStatus = 'beschikbaar' | 'beperkt_beschikbaar' | 'niet_beschikbaar' | 'alleen_op_uitnodiging' | 'tijdelijk_niet_actief';
export type EngagementStatus =
  | 'uitgenodigd' | 'bevestigd' | 'in_voorbereiding' | 'concept_gereed' | 'ter_beoordeling'
  | 'aanpassing_gevraagd' | 'goedgekeurd' | 'presentatie_gereed' | 'technisch_getest'
  | 'gereed_voor_dienst' | 'gesproken' | 'gearchiveerd' | 'geannuleerd';
export type InvitationStatus =
  | 'concept' | 'verstuurd' | 'bekeken' | 'geaccepteerd' | 'voorlopig_geaccepteerd'
  | 'afgewezen' | 'geannuleerd' | 'verlopen';
export type RecordVisibility = 'prive' | 'gedeeld';

export interface SpeakerPreferences {
  max_engagements_per_month: number | null;
  prefers_morning: boolean | null;
  prefers_evening: boolean | null;
  preparation_days_needed: number | null;
  locations: string[];
  audiences: string[];
  topics: string[];
}

export interface SpeakerProfile {
  id: string;
  organization_id: string;
  user_id: string | null;
  person_id: string | null;
  speaker_type: SpeakerType;
  display_name: string;
  biography: string | null;
  languages: string[];
  topics: string[];
  audiences: string[];
  availability_status: AvailabilityStatus;
  preferences: SpeakerPreferences;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SpeakerProfileInput {
  user_id?: string | null;
  speaker_type?: SpeakerType;
  display_name: string;
  biography?: string | null;
  availability_status?: AvailabilityStatus;
  is_active?: boolean;
}

export interface SpeakingEngagement {
  id: string;
  organization_id: string;
  speaker_id: string;
  co_speaker_ids: string[];
  service_type: string | null;
  title: string | null;
  working_title: string | null;
  theme: string | null;
  series_id: string | null;
  bible_references: string[];
  audience: string | null;
  language: string | null;
  location_id: string | null;
  start_at: string;
  end_at: string | null;
  target_duration_minutes: number | null;
  maximum_duration_minutes: number | null;
  status: EngagementStatus;
  invitation_status: InvitationStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SpeakingEngagementInput {
  speaker_id: string;
  title?: string | null;
  working_title?: string | null;
  theme?: string | null;
  start_at: string;
  end_at?: string | null;
  target_duration_minutes?: number | null;
  status?: EngagementStatus;
}

export interface SpeakerInvitationInput {
  speaker_id: string;
  email?: string | null;
  message?: string | null;
}

export interface SermonContent {
  id: string;
  organization_id: string;
  speaking_engagement_id: string;
  author_id: string;
  content_type: string | null;
  title: string | null;
  core_message: string | null;
  purpose: string | null;
  target_response: string | null;
  content: string | null;
  estimated_duration_minutes: number | null;
  visibility: RecordVisibility;
  shared_with_user_ids: string[];
  version: number;
  created_at: string;
  updated_at: string;
}

export interface SermonContentInput {
  title?: string | null;
  core_message?: string | null;
  content?: string | null;
  visibility?: RecordVisibility;
  shared_with_user_ids?: string[];
}

export interface SpeakerNote {
  id: string;
  organization_id: string;
  speaking_engagement_id: string;
  author_id: string;
  section_id: string | null;
  note: string;
  visibility: RecordVisibility;
  shared_with_user_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface SpeakerNoteInput {
  note: string;
  visibility?: RecordVisibility;
  shared_with_user_ids?: string[];
}

// ============== Fase 2: presentatiesjablonen, presentaties, slides, media ==============

/** 'style' = stijldefinitie; 'slides' = visueel sjabloon met gerenderde slides; 'editable' = bewerkbaar sjabloon (elementen). */
export type PresentationTemplateType = 'style' | 'slides' | 'editable';

export interface PresentationTemplate {
  id: string;
  organization_id: string;
  name: string;
  // Ontbreekt bij oudere sjablonen: dat zijn stijlsjablonen.
  template_type?: PresentationTemplateType;
  description?: string | null;
  slide_count?: number;
  preview_media_id?: string | null;
  // Alleen `editable`: de eerste slide, voor de bibliotheekweergave.
  preview_slide?: SlideVisual | null;
  // Herkomst van een sjabloon (nooit een token).
  source?: {
    type: 'google_slides' | 'presentation'; file_id?: string; name?: string; modified_time?: string | null;
    imported_at?: string; report?: ConversionReport;
  } | null;
  created_by?: string | null;
  font_family: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  logo_media_id: string | null;
  background_media_id: string | null;
  minimum_font_size_px: number | null;
  safe_margin_percent: number | null;
  supports_dark_mode: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface PresentationTemplateInput {
  name: string;
  font_family?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  logo_media_id?: string | null;
  background_media_id?: string | null;
  minimum_font_size_px?: number | null;
  safe_margin_percent?: number | null;
  supports_dark_mode?: boolean;
  is_default?: boolean;
}

export type PresentationType = 'slides' | 'canvas';

export type PresentationStatus = 'concept' | 'gereed' | 'technisch_getest' | 'gepubliceerd';

export type SlideType =
  | 'titelslide' | 'bijbeltekst' | 'verwijzing' | 'kernzin' | 'bulletpoints' | 'citaat'
  | 'afbeelding' | 'video' | 'audio' | 'vraag' | 'gebedspunt' | 'responsmoment'
  | 'zwart_scherm' | 'afsluitslide' | 'vrije_slide';

export type SlideVisibility =
  | 'alleen_spreker' | 'op_beamer' | 'delen_met_worship' | 'delen_met_dienstleider'
  | 'delen_met_presentatieteam' | 'openbaar_na_dienst';

export interface Presentation {
  id: string;
  organization_id: string;
  speaking_engagement_id: string;
  title: string | null;
  template_id: string | null;
  presentation_type: PresentationType;
  status: PresentationStatus;
  created_by: string;
  published_version: number;
  // Herkomst van een geïmporteerde presentatie; null/afwezig voor Studio.
  source?: PresentationSource | null;
  created_at: string;
  updated_at: string;
}

export interface PdfImportSource {
  type: 'pdf_import';
  filename: string;
  page_count: number;
  imported_at: string;
}

export interface GoogleSlidesSource {
  type: 'google_slides';
  file_id: string;
  name: string;
  modified_time: string | null;
  page_count: number;
  imported_at: string;
}

export interface TemplateSource {
  type: 'template';
  template_id: string;
  template_name: string;
  template_type?: 'editable';
  page_count: number;
  imported_at: string;
}

/** Wat bij de conversie niet of alleen bij benadering is overgenomen (per slide). */
export interface ConversionReportItem {
  slide: number;
  severity: 'lost' | 'approximated' | 'warning';
  kind: string;
  message: string;
  element?: string;
}

export interface ConversionReport {
  items: ConversionReportItem[];
  lost: number;
  approximated: number;
}

export interface GoogleSlidesEditableSource {
  type: 'google_slides_editable';
  file_id: string;
  name: string;
  modified_time: string | null;
  page_count: number;
  imported_at: string;
  report?: ConversionReport;
}

export type PresentationSource = PdfImportSource | GoogleSlidesSource | GoogleSlidesEditableSource | TemplateSource;

// ---- Bewerkbare slide-elementen (mirror van backend/sprekers/schemas.py) ----
// Alle maten staan in ontwerp-eenheden: een slide is altijd 1000 breed en `page_height` hoog.

export const SLIDE_DESIGN_WIDTH = 1000;
export const DEFAULT_PAGE_HEIGHT = 562.5;

export type SlideElementType = 'text' | 'image' | 'shape';
export type ShapeKind = 'rect' | 'round_rect' | 'ellipse' | 'line';
export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type TextAnchor = 'top' | 'middle' | 'bottom';
export type ImageFit = 'contain' | 'cover' | 'fill';

export interface TextRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  color?: string | null;
  font_size?: number | null;
  font_family?: string | null;
}

export interface TextParagraph {
  runs: TextRun[];
  align?: TextAlign;
  line_spacing?: number | null;
  bullet?: boolean;
}

export interface ElementText {
  paragraphs: TextParagraph[];
  anchor?: TextAnchor;
  padding?: number;
}

export interface ImageCrop {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

export interface SlideElement {
  id: string;
  type: SlideElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  opacity?: number;
  flip_x?: boolean;
  text?: ElementText | null;
  media_id?: string | null;
  fit?: ImageFit;
  crop?: ImageCrop | null;
  shape?: ShapeKind;
  fill?: string | null;
  stroke?: string | null;
  stroke_width?: number;
}

export interface SlideBackground {
  color?: string | null;
  media_id?: string | null;
  fit?: ImageFit;
}

/** Het visuele deel van een vrije slide; ook de vorm van `preview_slide` bij een bewerkbaar sjabloon. */
export interface SlideVisual {
  elements?: SlideElement[];
  background?: SlideBackground | null;
  page_height?: number | null;
}

export interface PresentationTemplateSlide {
  id: string;
  organization_id: string;
  template_id: string;
  position: number;
  media_id?: string | null;
  display_settings: Record<string, unknown>;
  elements?: SlideElement[];
  background?: SlideBackground | null;
  page_height?: number | null;
  created_at: string;
}

export interface PresentationInput {
  title?: string | null;
  template_id?: string | null;
  presentation_type?: PresentationType;
}

export interface PresentationUpdateInput {
  title?: string | null;
  template_id?: string | null;
  status?: PresentationStatus;
}

export interface PresentationSlide {
  id: string;
  organization_id: string;
  presentation_id: string;
  position: number;
  slide_type: SlideType;
  content: string | null;
  bible_reference: string | null;
  media_id: string | null;
  speaker_note_id: string | null;
  display_settings: Record<string, unknown>;
  visibility: SlideVisibility;
  estimated_duration_seconds: number | null;
  // Alleen voor `vrije_slide`.
  elements?: SlideElement[];
  background?: SlideBackground | null;
  page_height?: number | null;
  // Optimistic locking van de inhoud (+1 per wijziging van elements/background/page_height).
  revision?: number;
  created_at: string;
  updated_at: string;
}

export interface PresentationSlideInput {
  slide_type: SlideType;
  content?: string | null;
  bible_reference?: string | null;
  media_id?: string | null;
  visibility?: SlideVisibility;
  estimated_duration_seconds?: number | null;
  position?: number | null;
  elements?: SlideElement[];
  background?: SlideBackground | null;
  page_height?: number | null;
  /** Verplicht bij het wijzigen van elements/background/page_height: de laatst geziene revisie. */
  revision?: number;
}

export interface PresentationDetail extends Presentation {
  slides: PresentationSlide[];
}

// ============== Canvas Player (Stap 1) ==============
// Mirror van backend/sprekers/schemas.py -- zelfde bewuste duplicatie als de
// rest van dit bestand t.o.v. de Pydantic-schemas.

export type CanvasFrameShape = 'theme' | 'rounded' | 'panel' | 'large' | 'borderless' | 'circle';
export type CanvasPathStepType = 'overview' | 'focus';
export type FocusType = 'slide' | 'canvas_frame';

export interface CanvasPathStep {
  type: CanvasPathStepType;
  target_id: string | null;
}

export interface CanvasFrame {
  id: string;
  organization_id: string;
  board_id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  level: number;
  shape: CanvasFrameShape;
  parent_frame_id: string | null;
  rotation: number;
  eyebrow: string | null;
  title: string | null;
  subtitle: string | null;
  ref: string | null;
  body: string | null;
  media_id: string | null;
  media_type: string | null;
  speaker_note_id: string | null;
  revision: number;
  created_at: string;
  updated_at: string;
}

export interface CanvasBoard {
  id: string;
  organization_id: string;
  presentation_id: string;
  width: number;
  height: number;
  background: string | null;
  connections: string[][];
  path: CanvasPathStep[];
  theme_frame_id: string | null;
  revision: number;
  created_at: string;
  updated_at: string;
}

export interface CanvasBoardDetail extends CanvasBoard {
  frames: CanvasFrame[];
}

export interface CanvasFrameImportInput {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  level?: number;
  shape?: CanvasFrameShape;
  parent_frame_id?: string | null;
  rotation?: number;
  eyebrow?: string | null;
  title?: string | null;
  subtitle?: string | null;
  ref?: string | null;
  body?: string | null;
  media_id?: string | null;
  media_type?: string | null;
  speaker_note_id?: string | null;
}

export interface CanvasBoardImportInput {
  width?: number;
  height?: number;
  background?: string | null;
  frames: CanvasFrameImportInput[];
  connections?: string[][];
  path?: CanvasPathStep[];
  theme_frame_id?: string | null;
}

// ============== Canvas Editor (Stap 2) ==============

export interface CanvasFrameCreateInput {
  x: number;
  y: number;
  width: number;
  height: number;
  level?: number;
  shape?: CanvasFrameShape;
  parent_frame_id?: string | null;
  rotation?: number;
  eyebrow?: string | null;
  title?: string | null;
  subtitle?: string | null;
  ref?: string | null;
  body?: string | null;
  media_id?: string | null;
  media_type?: string | null;
  speaker_note_id?: string | null;
}

export interface CanvasFrameUpdateInput {
  revision: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  level?: number;
  shape?: CanvasFrameShape;
  parent_frame_id?: string | null;
  rotation?: number;
  eyebrow?: string | null;
  title?: string | null;
  subtitle?: string | null;
  ref?: string | null;
  body?: string | null;
  media_id?: string | null;
  media_type?: string | null;
  speaker_note_id?: string | null;
}

export interface CanvasBoardUpdateInput {
  revision: number;
  width?: number;
  height?: number;
  background?: string | null;
  connections?: string[][];
  path?: CanvasPathStep[];
  theme_frame_id?: string | null;
}

export interface CanvasTemplateSummary {
  id: string;
  name: string;
  description: string | null;
  is_builtin: boolean;
}

export interface CanvasTemplate {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  created_by: string;
  snapshot: CanvasBoardImportInput;
  created_at: string;
  updated_at: string;
}

export interface CanvasTemplateInput {
  name: string;
  description?: string | null;
  source_presentation_id: string;
}

export type MediaRights = 'eigen_materiaal' | 'toestemming_ontvangen' | 'rechtenvrij' | 'licentie_aanwezig' | 'onbekend';

export interface SpeakerMedia {
  id: string;
  organization_id: string;
  speaking_engagement_id: string | null;
  file_id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  title: string | null;
  description: string | null;
  rights: MediaRights;
  uploaded_by: string;
  uploaded_at: string;
}

// ============== Fase 3: live spreekmodus ==============

export type SessionStatus = 'wachtend' | 'actief' | 'beeindigd' | 'verlopen';

export interface PresentationSession {
  id: string;
  organization_id: string;
  presentation_id: string;
  speaking_engagement_id: string;
  controller_user_id: string | null;
  presenter_device_id: string | null;
  session_token: string;
  status: SessionStatus;
  current_slide_id: string | null;
  current_item_type: FocusType;
  current_item_id: string | null;
  current_path_index: number;
  // Alleen gespiegeld voor volledigheid met de backend -- weergavelogica
  // hoort is_overview uit het laatst ontvangen SSE-event/initiële fetch te
  // halen (canvasSelectors.resolveIsOverviewAndTarget), niet dit veld direct
  // te lezen als "de huidige weergavestatus" (zie applySessionEvent.ts).
  canvas_manual_overview: boolean;
  is_blackout: boolean;
  // Monotoon oplopend per sessie (backend: +1 per toestandswijziging). Laat
  // applySessionEvent een oud event negeren. Ontbreekt op sessies van vóór
  // deze versie = 0.
  state_version?: number;
  created_by: string;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
}

export interface SessionEvent {
  type: string;
  [key: string]: unknown;
}

// Los van PresentationSession.session_token (de QR-koppelcode) -- dit token
// geeft uitsluitend toegang tot de SSE-eventsroute van precies deze sessie,
// nooit tot andere REST-routes (zie backend/sprekers/service.py).
export interface SessionStreamToken {
  stream_token: string;
  expires_at: string;
}
